import {ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Output, ViewChild,} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {FormFieldGroupConfig} from "../../../../../model/api/form-field-group-config";
import {CcBaseComponent} from "../../../../../crystal-elements/cc-base.component";
import {FieldRecord} from "../../../../../model/api/field-record";
import {FieldRequest} from "../../../../../model/api/field-request";
import {CommonFieldService} from "../../../../../services/common/common-field.service";
import {ExchangeDetail} from "../../../../../model/exchange/exchange-detail";
import {Customer} from "../../../../../model/customer/customer";
import {AccountDefinition} from "../../../../../model/customer/account-definition";
import {AllocationRequest} from "../../../../../model/clearing/allocation/allocation-request";
import {AccountType} from "../../../../../model/exchange/account-type";
import {ClearingAllocationService} from "../../../../../services/clearing/clearing-allocation.service";
import {Owner} from "../../../../../model/customer/owner";
import {Trade} from "../../../../../model/clearing/trade";
import {CcFormDialogComponent} from "../../../../../crystal-elements/form-dialog/cc-form-dialog.component";
import {Trader} from "../../../../../model/customer/trader";
import {AllocationSplitRequest} from "../../../../../model/clearing/allocation/allocation-split-request";
import {AccountTrader} from "../../../../../model/customer/account-trader";
import {ClearingSelectionSummary} from "../../../../../model/clearing/clearing-selection-summary";
import {ClearingInfoLoadRequest} from "../../../../../model/clearing/clearing-info-load-request";
import {Observable, Subscription} from "rxjs";
import {ApiValidation} from "../../../../../model/api/api-validation";
import {AppConfigService} from "../../../../../services/app-config.service";
import {BrokerageTable} from "../../../../../model/customer/brokerage-table";
import {ExecTrader} from "../../../../../model/customer/exec-trader";
import {MoneyOwner} from "../../../../../model/customer/money-owner";
import Side = Trade.Side;
import OpenClose = AccountDefinition.OpenClose;
import {ProgressTracker} from "../../../../../model/api/progress-tracker";
import {FieldsService} from "../../../../../services/common/fields-service";
import {StringUtils} from "../../../../../utils/stringUtils";
import {CcStorageService} from "../../../../../services/common/cc-storage.service";
import {CCRoles} from "../../../../../model/security/cc-roles-type";

@Component({
  template: '',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ]
})
export class AllocationFormBaseComponent extends CcBaseComponent {

  protected formContractsCodes: string[] = [];

  traders: FieldRecord<Trader>[] = [];
  accountTraders: FieldRecord<AccountTrader>[] = [];
  brokerageTables: FieldRecord<BrokerageTable>[] = [];
  owners: FieldRecord<Owner>[] = [];
  exchanges: FieldRecord<ExchangeDetail>[] = []
  customers: FieldRecord<Customer>[] = [];
  execTraders: FieldRecord<ExecTrader>[] = [];
  moneyOwners: FieldRecord<MoneyOwner>[] = [];
  displayedCustomers: FieldRecord<Customer>[] = [];

  originAccounts: FieldRecord<AccountDefinition>[] = [];
  accounts: FieldRecord<AccountDefinition>[] = [];

  accountTypes: FieldRecord<any>[] = [];
  displayedAccountTypes: FieldRecord<any>[] = [];
  displayedGUpAccountTypes: FieldRecord<any>[] = [];

  openCloses: FieldRecord<any>[];

  transPartKey: string = 'clearing.trade';

  formLoading: boolean = false;

  formSubmitted: boolean = true;

  formConfig: FormFieldGroupConfig[] = [];

  dialogFormTitle: string = '';

  formRecord: AllocationRequest | any;

  loadingProgressValue: number = 0;
  dialogFormVisible: boolean = false;

  @Output()
  onSavedForm = new EventEmitter<any>();

  @ViewChild('ccAllocationFormDialog')
  ccFormDialog: CcFormDialogComponent;

  currentExchanges: ExchangeDetail[] = [];

  previousAccountAlert: string = '';
  previousGiveupMssgAlert: string = '';
  originTraderMemo: string = '';
  previousTraderMemo: string = '';

  tracker: ProgressTracker;

  protected summaryBuyValue: ClearingSelectionSummary = new ClearingSelectionSummary(Side.BUY);

  protected summarySellValue: ClearingSelectionSummary = new ClearingSelectionSummary(Side.SELL);

  private selectionSummarySubscription?: Subscription;
  private selectionSummaryRequestVersion: number = 0;

  constructor(protected el: ElementRef,
              cdref: ChangeDetectorRef,
              protected allocationService: ClearingAllocationService,
              protected commonFieldService: CommonFieldService,
              messageService: MessageService,
              protected confirmationService: ConfirmationService,
              translateService: TranslateService,
              protected fieldsService: FieldsService,
              protected storageService: CcStorageService,
              protected appConfigService: AppConfigService,) {
    super(cdref, messageService, translateService);
  }

  protected initConfigs() {
    super.initConfigs();
    this.refreshConfig();
  }

  public loadData() {
    // init data
    this.loadingProgressValue = 0;

    this.tracker = this.fieldsService.createTracker();

    this.tracker.progress.subscribe(value => {
      this.loadingProgressValue = value;
      this.refreshFieldData();
    });

    this.openCloses = Object.keys(AccountDefinition.OpenClose).map(key => new FieldRecord(key, StringUtils.humanize(key)));
    this.fieldsService.loadField(this.owners, () => this.commonFieldService.getOwners(FieldRequest.DEFAULT()), this.tracker, (records) => {
      this.ownersLoaded();
    });
    this.fieldsService.loadField(this.exchanges, () => this.commonFieldService.getExchangeCodes(FieldRequest.DEFAULT()), this.tracker, (records) => {
      this.refreshFieldData();
      this.filterExchanges();
    });
    this.fieldsService.loadField(this.customers, () => this.commonFieldService.getCustomers(FieldRequest.DEFAULT()), this.tracker, (records) => {
      this.filterCustomers();
    });
    this.fieldsService.loadField(this.originAccounts, () => this.commonFieldService.getAccounts(FieldRequest.DEFAULT()), this.tracker, (records) => {
      this.filterAccountFromCustomers();
    });
    this.fieldsService.loadField(this.accountTypes, () => this.commonFieldService.getAccountTypes(FieldRequest.DEFAULT()), this.tracker, (records) => {
      this.filterAccountTypes();
    });
    this.fieldsService.loadField(this.traders, () => this.commonFieldService.getTraders(FieldRequest.DEFAULT()), this.tracker);
    this.fieldsService.loadField(this.accountTraders, () => this.commonFieldService.getAccountTraders(FieldRequest.DEFAULT()), this.tracker);
    this.fieldsService.loadField(this.brokerageTables, () => this.commonFieldService.getBrokerageTables(FieldRequest.DEFAULT()), this.tracker);
    if (this.appConfigService.billingEnabled) {
      this.fieldsService.loadField(this.execTraders, () => this.commonFieldService.getExecTraders(FieldRequest.DEFAULT()), this.tracker, (records) => {
        this.execTraders = this.execTraders.filter(field => {
          return !this.formRecord.owner || this.formRecord.owner === field.value.owner;
        });
        this.refreshFieldData();
        this.formRecord.allocations.forEach(allocation => this.setBrokerageRate(allocation));
      });
    }
    this.fieldsService.loadField(this.moneyOwners, () => this.commonFieldService.getMoneyOwner(FieldRequest.DEFAULT()), this.tracker, (records) => {
      this.moneyOwners = this.moneyOwners.filter(field => {
        return !this.formRecord.owner || this.formRecord.owner === field.value.owner;
      });
      this.refreshFieldData();
      this.formRecord.allocations.forEach(allocation => this.setBrokerageRate(allocation));
    });
  }

  protected filterExchanges() {
    this.currentExchanges.splice(0);
    if (this.exchanges && this.formRecord) {
      if (this.formRecord.allocations && this.formRecord.allocations.length > 0) {
        this.formRecord.allocations.forEach(allocation => {
            const exchangeField = this.exchanges ? this.exchanges.find(value => value.id === allocation.exchangeCode) : undefined;
            // if (exchangeField && !exchangeField.value.openCloseManagement) allocation.openClose = AccountDefinition.OpenClose.NO;
            if (exchangeField && !this.currentExchanges.find(value => value.code === exchangeField.value.code)) {
              this.currentExchanges.push(exchangeField.value);
            }
          }
        );
      } else {
        const exchangeField = this.exchanges ? this.exchanges.find(value => value.id === this.formRecord.exchangeCode) : undefined;
        if (exchangeField && !exchangeField.value.openCloseManagement) this.formRecord.openClose = AccountDefinition.OpenClose.NO;
        if (exchangeField && !this.currentExchanges.find(value => value.code === exchangeField.value.code)) {
          this.currentExchanges.push(exchangeField.value);
        }
      }
    }
  }

  protected filterCustomers(): FieldRecord<Customer>[] {
    if (this.customers && this.formRecord) {
      const owner = this.owners.find(value => value.value.code === this.formRecord.owner);
      this.displayedCustomers = this.customers.filter(field => {
        return !this.formRecord.owner || !owner || this.formRecord.owner === field.value.owner;
      });
    } else {
      this.displayedCustomers = [];
    }
    this.filterAccountFromCustomers();
    return this.displayedCustomers;
  }

  private filterAccountFromCustomers() {
    if (this.originAccounts && this.displayedCustomers && this.originAccounts.length > 0 && this.displayedCustomers.length > 0) {
      this.accounts.splice(0);
      this.originAccounts.forEach(field => {
        const exchange: ExchangeDetail = this.currentExchanges.find(value => value.clearingHouse === field.value.clearingHouse
          && (!field.value.exchangeCode || field.value.exchangeCode === ExchangeDetail.ALL || value.code === field.value.exchangeCode));
        const customerField =
          this.displayedCustomers.find(customer => customer.value.id == field.value.customerId && exchange);
        if (customerField && customerField.value.enabled) {
          this.accounts.push(field);
        }
      });
    }
  }

  protected filterAccounts(allocationRecord: AllocationSplitRequest, query?: string) {
    let tmpDisplayedAccounts = [];
    if (allocationRecord && this.accounts && this.formRecord) {
      let customerId = undefined;
      if (!query) {
        customerId = allocationRecord.customerId;
        if (!customerId && allocationRecord.customerName && this.customers) {
          const customerField = this.customers.find(customer => customer.text === allocationRecord.customerName);
          if (customerField)
            customerId = customerField.id;
        }
      }
      tmpDisplayedAccounts = this.accounts.filter(field => {
        return allocationRecord.clearingHouse === field.value.clearingHouse
          && (!this.formRecord.owner || this.formRecord.owner === field.value.owner)
          && (!field.value.exchangeCode
            || field.value.exchangeCode === ExchangeDetail.ALL
            || allocationRecord.exchangeCode === field.value.exchangeCode)
          && (!customerId || '' + field.value.customerId === customerId)
          && (!query || field.value.code.toLowerCase().startsWith(query.toLowerCase()))
          ; /* this.formRecord.owner === field.value.owner
          && */
      });
    }
    return tmpDisplayedAccounts;
  }

  protected filterAccountTypes() {
    let defaultGUType = FieldRecord.DEFAULT();
    defaultGUType.text = this.translateService.instant('page.account.form.defaultAccountTypeGup');
    defaultGUType.id = AccountType.GIVE_UP_CODE;
    if (this.accountTypes && this.formRecord) {
      this.displayedAccountTypes = this.accountTypes.filter(field =>
        field.id === AccountType.GIVE_UP_CODE || (
          (this.formRecord.clearingHouse === field.value.clearingHouse
            && (!field.value.exchangeCode || field.value.exchangeCode.length === 0
              || field.value.exchangeCode === ExchangeDetail.ALL
              || field.value.exchangeCode === ExchangeDetail.ALL_TEXT
              || this.formRecord.exchangeCode === field.value.exchangeCode))));
      this.displayedGUpAccountTypes = [...this.displayedAccountTypes];
      this.displayedAccountTypes.unshift(defaultGUType);
    } else {
      this.displayedAccountTypes = [defaultGUType];
      this.displayedGUpAccountTypes = [];
    }
  }

  protected filterTraders(allocationRecord: AllocationSplitRequest): FieldRecord<Trader>[] {
    if (allocationRecord && allocationRecord.customerName) {
      const customerField: FieldRecord<Customer> = this.customers.find(cust => cust.text == allocationRecord.customerName);
      if (customerField && customerField.value && customerField.value.traders && customerField.value.traders.length > 0) {
        const traderCodes = customerField.value.traders.map(trader => trader.code);
        return this.traders.filter(trader => traderCodes.includes(trader.id));
      }
    }
    return this.traders
  }

  closeDialogForm(): void {
    this.dialogFormVisible = false;
    this.markForCheck();
  }

  protected refreshConfig(): void {
  }

  public refreshFieldData(): void {
    if (this.loadingProgressValue >= 100) {
      this.refreshConfig();
    }
    this.ccFormDialog.reloadFieldValueMap();
    this.markForCheck();
    // this.loadingProgressValue = tmpLoadingProgressValue;
  }

  protected getTraderNameFromAccountTraders(accountDef: AccountDefinition) {
    let traderName = accountDef.traderName;
    if (!traderName) {
      const accountTrader = this.accountTraders.find(aTrader => aTrader.value.accountCode === accountDef.code
        && aTrader.value.owner === accountDef.owner
        && aTrader.value.customerName === accountDef.customerName
        && ((!accountDef.exchangeCode || ExchangeDetail.ALL_TEXT === accountDef.exchangeCode) ||
          (!aTrader.value.exchangeCode || ExchangeDetail.ALL_TEXT === aTrader.value.exchangeCode || aTrader.value.exchangeCode === accountDef.exchangeCode)));

      if (accountTrader) {
        let hasContract = true;
        if (accountTrader.value.contractListInclude) {
          this.formContractsCodes.forEach(contract => {
            if (!accountTrader.value.contractList.split(',').includes(contract)) hasContract = false;
          });
        }
        if (hasContract) {
          const trader = this.accountTraders.find(trader => trader.value.traderCode === accountTrader.value.traderCode)
          if (trader) {
            traderName = trader.text;
          }
        }
      }
    }
    return traderName;
  }

  protected manageGiveUpMssg23(record?): boolean {
    if (record) {
      const exchField = this.exchanges ? this.exchanges.find(field => field.value
        && field.value.clearingHouse === record.clearingHouse
        && field.value.code === record.exchangeCode) : undefined;
      return exchField && exchField.value.giveupMessage2Management;
    } else {
      let manageGiveUpMssg23 = this.currentExchanges && this.currentExchanges.length > 0;
      this.currentExchanges.forEach(value => {
        if (!value.giveupMessage2Management)
          manageGiveUpMssg23 = false;
      });
      return manageGiveUpMssg23;
    }
  }

  protected manageTraderMemo(record?): boolean {
    if (record) {
      const exchField = this.exchanges ? this.exchanges.find(field => field.value
        && field.value.clearingHouse === record.clearingHouse
        && field.value.code === record.exchangeCode) : undefined;
      return exchField && exchField.value.traderMemoManagement;
    } else {
      let manageTraderMemo = this.currentExchanges && this.currentExchanges.length > 0;
      this.currentExchanges.forEach(value => {
        if (!value.traderMemoManagement)
          manageTraderMemo = false;
      });
      return manageTraderMemo;
    }
  }

  protected apsManagement(): boolean {
    let apsManagement = this.currentExchanges && this.currentExchanges.length > 0;
    this.currentExchanges.forEach(value => {
      if (!value.apsManagement)
        apsManagement = false;
    });
    return apsManagement;
  }

  protected openCloseManagement(): boolean {
    let openCloseManagement = this.currentExchanges && this.currentExchanges.length > 0;
    this.currentExchanges.forEach(value => {
      if (!value.openCloseManagement)
        openCloseManagement = false;
    });
    return openCloseManagement;
  }

  protected forceCustomerValues(record, customerName, customerId): void {
    if (customerId) {
      record.customerName = customerName;
      record.customerId = customerId;
    } else {
      record.customerName = undefined;
      record.customerId = undefined;
    }
    record.accountCode = undefined;
    record.accountTypeCode = undefined;
    record.giveupAccountType = AccountType.GIVE_UP_CODE;
    record.executionFee = undefined;
    record.giveUpBrokerCode = undefined;
    record.giveUpCommission = undefined;
    record.giveUpMessage = undefined;
    record.giveUpMessage2 = undefined;
    record.giveUpMessage3 = undefined;
    record.broker = undefined;
    record.openClose = OpenClose.NO;
    record.reference1 = undefined;
    record.reference2 = undefined;
    record.reference3 = undefined;
    record.traderName = undefined;
    if (this.previousTraderMemo) {
      this.formRecord.traderMemo = this.originTraderMemo;
      this.previousTraderMemo = undefined;
    }
    this.accounts.splice(0);
    this.forceAccountValues(record, '')
    this.ccFormDialog.reloadFieldValueMap();
  }

  protected forceAccountValues(record, accountCode): Customer {
    if (this.accounts.length == 0) {
      this.filterAccountFromCustomers();
    }
    let customer: Customer = undefined;
    const accountField = this.accounts.find(field =>
      (!this.formRecord.owner || this.formRecord.owner === field.value.owner)
      && (!record.exchangeCode || record.exchangeCode === field.value.exchangeCode
        || (!field.value.exchangeCode && record.clearingHouse == field.value.clearingHouse))
      && field.text?.toLowerCase() === accountCode?.toLowerCase() /*|| field['select']*/);
    if (accountField) {
      const accountDef = {...accountField.value};
      record.accountCode = accountDef.code;
      record.accountTypeCode = accountDef.accountTypeCode;
      record.giveupAccountType = accountDef.giveupAccountType;
      record.executionFee = accountDef.executionFee;
      record.giveUpBrokerCode = accountDef.giveUpBrokerCode;
      record.giveUpCommission = accountDef.giveUpCommission;
      record.giveUpMessage = accountDef.giveUpMessage ? accountDef.giveUpMessage.toUpperCase() : '';
      if (this.manageGiveUpMssg23(record)) {
        record.giveUpMessage2 = accountDef.giveupMessage2;
        record.giveUpMessage3 = accountDef.giveupMessage3;
      } else {
        record.giveUpMessage2 = undefined;
        record.giveUpMessage3 = undefined;
      }
      record.broker = accountDef.giveupMessage3;
      record.openClose = accountDef.openCloseForFutures;
      record.reference1 = accountDef.reference1 ? accountDef.reference1.toUpperCase() : '';
      record.reference2 = accountDef.reference2;
      record.reference3 = accountDef.reference3;

      const customerField = this.customers.find(customer => customer.id == '' + accountDef.customerId);
      if (customerField) {
        record.customerName = customerField.text;
        accountDef.customerName = customerField.text;
        record.customerId = customerField.id;
        customer = customerField.value;
      }
      const traderName = this.getTraderNameFromAccountTraders(accountDef);
      if (this.appConfigService.iceTraderMemoFeature && this.manageTraderMemo()) {
        if (accountDef.bindTraderToMemo) {
          this.formRecord.traderMemo = traderName?.toUpperCase().substring(0, 20);
          this.previousTraderMemo = this.formRecord.traderMemo;
        } else if (this.previousTraderMemo == this.formRecord.traderMemo) {
          this.formRecord.traderMemo = this.originTraderMemo;
          this.previousTraderMemo = undefined;
        } else {
          this.previousTraderMemo = undefined;
        }
      } else {
        this.formRecord.traderMemo = undefined;
        this.previousTraderMemo = undefined;
      }
      record.traderName = traderName;
    } else if (!accountCode || accountCode === '') {
      record.accountCode = undefined;
      record.accountTypeCode = AccountType.GIVE_UP_CODE;
      record.giveupAccountType = undefined;
      record.executionFee = undefined;
      record.giveUpBrokerCode = undefined;
      record.giveUpCommission = undefined;
      record.giveUpMessage = undefined;
      record.giveUpMessage2 = undefined;
      record.giveUpMessage3 = undefined;
      record.broker = undefined;
      record.openClose = undefined;
      record.reference1 = undefined;
      record.reference2 = undefined;
      record.reference3 = undefined;
      record.traderName = undefined;
      if (this.previousTraderMemo) {
        this.formRecord.traderMemo = this.originTraderMemo;
        this.previousTraderMemo = undefined;
      }
    } else if (!accountField) {
      record.accountTypeCode = AccountType.GIVE_UP_CODE;
      record.giveupAccountType = undefined;
      record.executionFee = undefined;
      record.giveUpBrokerCode = undefined;
      record.giveUpCommission = undefined;
      record.giveUpMessage = undefined;
      record.giveUpMessage2 = undefined;
      record.giveUpMessage3 = undefined;
      record.broker = undefined;
      record.openClose = undefined;
      record.reference1 = undefined;
      record.reference2 = undefined;
      record.reference3 = undefined;
      record.traderName = undefined;
      if (this.previousTraderMemo) {
        this.formRecord.traderMemo = this.originTraderMemo;
        this.previousTraderMemo = undefined;
      }
    }
    this.setBrokerageRate(record);
    this.setMoneyOwner(record);
    if (this.appConfigService.clearingAllocationAccountGiveupMssgAgreeWarning
      && record && record.giveUpMessage && record.accountCode && record.giveUpMessage != record.accountCode
      && this.previousAccountAlert != record.accountCode && this.previousGiveupMssgAlert != record.giveUpMessage) {
      this.previousAccountAlert = record.accountCode;
      this.previousGiveupMssgAlert = record.giveUpMessage;
      this.ccFormDialog.ccForm.warnMessage("The account and GU Message does not agree");
    }
    return customer;
  }

  setBrokerageRate(record: any) {

    const execTraderField = this.execTraders.find(execTrader => execTrader.text == record.execTrader);
    const execTrader = execTraderField ? execTraderField.value : undefined;

    const customerField = this.customers.find(customer => customer.id == '' + record.customerId);
    const customer = customerField ? customerField.value : undefined;

    const accountField = this.accounts.find(field =>
      (!this.formRecord.owner || this.formRecord.owner === field.value.owner)
      && (!record.exchangeCode || record.exchangeCode === field.value.exchangeCode
        || (!field.value.exchangeCode && record.clearingHouse == field.value.clearingHouse))
      && record.accountCode && field.text.toLowerCase() === record.accountCode.toLowerCase() /*|| field['select']*/);
    const account = accountField ? accountField.value : undefined;

    let brokerageTable = undefined;
    if (execTrader) {
      brokerageTable = this.brokerageTables
        .find(field => field.value.ownerCode === execTrader.owner
          && field.value.brokerageTableName === execTrader.brokerageTableName
          && (!field.value.exchangeCode || record.exchangeCode === field.value.exchangeCode)
          && (!field.value.clearingHouse || record.clearingHouse == field.value.clearingHouse)
          && ((this.formRecord.strike == 0 && field.value.allFutures) || (this.formRecord.strike != 0 && field.value.allOptions))
          && (!field.value.contractCode || field.value.contractCode.split(',').findIndex(value => value === this.formRecord.contractCode) > -1
            || this.areAllStringsPresent(this.formContractsCodes, field.value.contractCode.split(','))));
    }
    if (account && !brokerageTable) {
      brokerageTable = this.brokerageTables
        .find(field => field.value.ownerCode === account.owner
          && field.value.brokerageTableName === account.agreementId
          && (!field.value.exchangeCode || record.exchangeCode === field.value.exchangeCode)
          && (!field.value.clearingHouse || record.clearingHouse == field.value.clearingHouse)
          && ((this.formRecord.strike == 0 && field.value.allFutures) || (this.formRecord.strike != 0 && field.value.allOptions))
          && (!field.value.contractCode || field.value.contractCode.split(',').findIndex(value => value === this.formRecord.contractCode) > -1
            || this.areAllStringsPresent(this.formContractsCodes, field.value.contractCode.split(','))));
    }
    if (customer && !brokerageTable) {
      brokerageTable = this.brokerageTables
        .find(field => field.value.ownerCode === customer.owner
          && field.value.brokerageTableName === customer.brokerageTableName
          && (!field.value.exchangeCode || record.exchangeCode === field.value.exchangeCode)
          && (!field.value.clearingHouse || record.clearingHouse == field.value.clearingHouse)
          && ((this.formRecord.strike == 0 && field.value.allFutures) || (this.formRecord.strike != 0 && field.value.allOptions))
          && (!field.value.contractCode || field.value.contractCode.split(',').findIndex(value => value === this.formRecord.contractCode) > -1
            || this.areAllStringsPresent(this.formContractsCodes, field.value.contractCode.split(','))));
    }

    if (brokerageTable) {
      record.rate = brokerageTable.value.amountPerLot;
      record.currency = brokerageTable.value.currency;
    } else {
      // record.rate = undefined;
      // record.currency = undefined;
    }
  }

  setMoneyOwner(record: any) {

    const accountField = this.accounts.find(field => (!record.exchangeCode || record.exchangeCode === field.value.exchangeCode
        || (!field.value.exchangeCode && record.clearingHouse == field.value.clearingHouse))
      && record.accountCode && field.text.toLowerCase() === record.accountCode.toLowerCase() /*|| field['select']*/);
    const account = accountField ? accountField.value : undefined;

    let moneyOwner = undefined;
    if (account) {
      moneyOwner = this.moneyOwners
        .find(field => field.value.owner === account.owner
          && field.value.code === account.moneyOwner
        );
    }

    if (moneyOwner) {
      record.moneyOwner = moneyOwner.value.code;
    } else {
      // record.moneyOwner = undefined;
    }
  }

  private areAllStringsPresent(sourceArray: string[], targetArray: string[]): boolean {
    for (let item of sourceArray) {
      if (!targetArray.includes(item)) {
        return false;
      }
    }
    return true;
  }

  ngOnDestroy(): void {
    this.selectionSummarySubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  protected loadFooterInfos(itemIds: string[]) {
    this.summaryBuyValue = new ClearingSelectionSummary(Side.BUY);
    this.summarySellValue = new ClearingSelectionSummary(Side.SELL);
    this.markForCheck();
    const summaryRequest: ClearingInfoLoadRequest = new ClearingInfoLoadRequest(itemIds);
    const requestVersion = ++this.selectionSummaryRequestVersion;

    this.selectionSummarySubscription?.unsubscribe();
    this.selectionSummarySubscription = this.getSelectionSummary(summaryRequest).subscribe(apiValidation => {
      if (requestVersion !== this.selectionSummaryRequestVersion) {
        return;
      }

      if (apiValidation.success) {
        const summaryBuyValueTmp = apiValidation.item.find(value => value.side == Side.BUY);
        if (summaryBuyValueTmp)
          this.summaryBuyValue = summaryBuyValueTmp
        const summarySellValueTmp = apiValidation.item.find(value => value.side == Side.SELL);
        if (summarySellValueTmp)
          this.summarySellValue = summarySellValueTmp
      }
      this.markForCheck();
    });
  }

  protected getSelectionSummary(summaryRequest: ClearingInfoLoadRequest): Observable<ApiValidation<ClearingSelectionSummary[]>> {
    return undefined;
  }

  protected show() {
    this.refreshApsClass();
    this.dialogFormVisible = true;
    this.markForCheck();
  }

  protected hide() {
    this.dialogFormVisible = false;
    this.markForCheck();
  }


  protected refreshApsClass() {
    if (this.ccFormDialog.dialogClass.includes('cc-form-dialog-aps-active')) {
      this.ccFormDialog.dialogClass = this.ccFormDialog.dialogClass.replace('cc-form-dialog-aps-active', '');
    }
    if (this.formRecord && this.formRecord.apsGroupId && this.formRecord.apsGroupId.trim().length > 0) {
      if (!this.ccFormDialog.dialogClass.includes('cc-form-dialog-aps-active'))
        this.ccFormDialog.dialogClass = this.ccFormDialog.dialogClass + ' cc-form-dialog-aps-active';
    } else if (this.formRecord && this.formRecord['allocations'] && this.formRecord['allocations'].length > 0) {
      this.formRecord.allocations.forEach(formRecord => {
        if (formRecord && formRecord.apsGroupId && formRecord.apsGroupId.trim().length > 0) {
          if (!this.ccFormDialog.dialogClass.includes('cc-form-dialog-aps-active'))
            this.ccFormDialog.dialogClass = this.ccFormDialog.dialogClass + ' cc-form-dialog-aps-active';
        }
      });
    }
  }

  protected ownersLoaded() {

  }

  protected isRoleTraderMemoUpdate() {
    return this.hasRoles(CCRoles.ROLES_CLEARING_TRADER_MEMO_UPDATE)
  }

  save() {
    if (this.formRecord?.tradeIds?.length > this.appConfigService.allocationWarningLimitCount
      || this.formRecord?.orderIds?.length > this.appConfigService.allocationWarningLimitCount) {
      const message = this.formRecord?.orderIds ?
        this.getTranslation('page.' + this.transPartKey + '.dialog.confirm-update-warn', {orderCount: this.formRecord?.orderIds?.length})
        : this.getTranslation('page.' + this.transPartKey + '.dialog.confirm-allocate-warn', {tradeCount: this.formRecord?.tradeIds?.length});
      this.confirmationService.confirm({
        header: ' ',
        message: message,
        icon: 'fas fa-exclamation-triangle',
        acceptLabel: this.getTranslation('common.dialog.ok'),
        rejectLabel: this.getTranslation('common.dialog.cancel'),
        acceptButtonStyleClass: 'p-button-danger p-button-sm',
        rejectButtonStyleClass: 'p-button-warning p-button-sm',
        accept: () => {
          this.doSave();
        }
      });
    } else {
      this.doSave();
    }
  }

  protected doSave() {

  }
}
